import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Inbox as InboxIcon,
  Archive,
  Paperclip,
  AlertCircle,
  CheckCircle,
  PenSquare,
  Plug,
  Send,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/dialogs'
import NewAccountModal from '../components/email/NewAccountModal'
import ReconnectAccountModal from '../components/email/ReconnectAccountModal'
import { useAuth } from '../contexts/AuthContext'
import MessageReader from '../components/email/MessageReader'
import EmailComposer from '../components/comms/EmailComposer'
import {
  type EmailAccountDto,
  type EmailListItem,
  type EmailListResponse,
} from '@toga/shared'

/**
 * «sent» no es un filtro más: los otros cuatro acotan lo RECIBIDO (leído,
 * archivado…) y este cambia de carpeta. Por eso al activarlo no se arrastran
 * `isRead` ni `archived` — ver `loadMessages`.
 */
type Filter = 'all' | 'unread' | 'archived' | 'unarchived' | 'sent'

/**
 * «Ana Pérez <ana@x.com>, soporte@y.com» → «Ana Pérez, soporte@y.com».
 * En una fila de 30 caracteres el nombre dice mucho más que la dirección.
 */
function personas(addresses: string[]): string {
  return addresses
    .map((a) => a.replace(/<.+>/, '').trim() || a)
    .filter(Boolean)
    .join(', ')
}

const PAGE_SIZE = 50

export default function Inbox() {
  const auth = useAuth()
  // Reconectar abre una conexión saliente: el servidor solo lo admite a quien
  // puede escribir, así que a LECTURA no se le enseña un botón que da 403.
  const canReconnect = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const { t } = useTranslation('email')
  const [accounts, setAccounts] = useState<EmailAccountDto[] | null>(null)
  const [accountFilter, setAccountFilter] = useState<string>('') // '' = todas
  const [filter, setFilter] = useState<Filter>('all')
  const [q, setQ] = useState('')
  const [data, setData] = useState<EmailListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNewAccount, setShowNewAccount] = useState(false)
  // F43 — Redactar desde el Buzón: envía por la cuenta seleccionada (o la primera).
  const [composeOpen, setComposeOpen] = useState(false)
  const [syncBusy, setSyncBusy] = useState<string | null>(null)

  const loadAccounts = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: EmailAccountDto[] }>('/email-accounts')
      setAccounts(items)
    } catch (err) {
      if (err instanceof ApiError) {
        // 401 etc., dejamos accounts vacío
      }
      setAccounts([])
    }
  }, [])

  const loadMessages = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (accountFilter) params.set('accountId', accountFilter)
      if (filter === 'unread') params.set('isRead', 'false')
      if (filter === 'archived') params.set('archived', 'true')
      if (filter === 'unarchived') params.set('archived', 'false')
      if (filter === 'sent') params.set('direction', 'OUTBOUND')
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<EmailListResponse>(`/emails?${params.toString()}`)
      setData(resp)
    } catch {
      setData({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE })
    } finally {
      setLoading(false)
    }
  }, [q, accountFilter, filter])

  useEffect(() => {
    void loadAccounts()
  }, [loadAccounts])

  useEffect(() => {
    void loadMessages()
  }, [loadMessages])

  // Cuenta cuya reconexión se está pidiendo (null = modal cerrado).
  const [reconnecting, setReconnecting] = useState<EmailAccountDto | null>(null)

  const onSync = async (accountId: string) => {
    setSyncBusy(accountId)
    try {
      await api.post(`/email-accounts/${accountId}/sync`)
      // Damos un par de segundos al worker.
      setTimeout(() => {
        void loadAccounts()
        void loadMessages()
      }, 3500)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('syncError'))
    } finally {
      setTimeout(() => setSyncBusy(null), 3500)
    }
  }

  // Si no hay cuentas, pantalla de onboarding.
  if (accounts !== null && accounts.length === 0) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-10 text-center animate-slideUp">
        <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center bg-toga-500/15 text-toga-700 dark:text-toga-300 shadow-glow-toga mb-4">
          <Mail size={28} />
        </div>
        <h2 className="text-2xl font-serif font-semibold t-primary mb-2">
          {t('onboarding.title')}
        </h2>
        <p className="t-muted text-sm mb-5">
          {t('onboarding.description')}
        </p>
        <button
          type="button"
          onClick={() => setShowNewAccount(true)}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga inline-flex items-center gap-2"
        >
          <Plus size={16} /> {t('connectAccount')}
        </button>
        {showNewAccount && (
          <NewAccountModal
            onClose={() => setShowNewAccount(false)}
            onCreated={() => {
              setShowNewAccount(false)
              void loadAccounts()
              void loadMessages()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_360px_1fr] gap-4 h-[calc(100vh-9rem)] animate-slideUp">
      {/* Sidebar de cuentas y filtros */}
      <aside className="surface p-3 overflow-y-auto">
        <button
          type="button"
          onClick={() => setComposeOpen(true)}
          className="w-full mb-3 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center justify-center gap-2"
        >
          <PenSquare size={14} /> {t('compose')}
        </button>
        <div className="flex items-center justify-between mb-2 px-1">
          <h3 className="text-xs uppercase tracking-wider t-faint">{t('accounts')}</h3>
          <button
            type="button"
            onClick={() => setShowNewAccount(true)}
            className="w-7 h-7 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
            title={t('connectAccount')}
          >
            <Plus size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setAccountFilter('')}
          className={[
            'w-full text-left px-2.5 py-1.5 rounded-lg text-sm flex items-center gap-2 transition mb-2',
            !accountFilter ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200' : 't-secondary hover:bg-hover',
          ].join(' ')}
        >
          <InboxIcon size={14} /> {t('allAccounts')}
        </button>
        {accounts?.map((a) => (
          <div
            key={a.id}
            className={[
              'rounded-lg px-2.5 py-1.5 mb-1',
              accountFilter === a.id ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200' : 't-secondary',
            ].join(' ')}
          >
            <div className="flex items-center justify-between gap-1">
              <button
                type="button"
                onClick={() => setAccountFilter(a.id)}
                className="text-sm text-left truncate flex-1 min-w-0"
                title={a.emailAddress}
              >
                <div className="truncate">{a.displayName || a.emailAddress}</div>
                <div className="text-[11px] t-faint truncate">{a.emailAddress}</div>
              </button>
              <button
                type="button"
                onClick={() => onSync(a.id)}
                disabled={syncBusy === a.id}
                className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-faint hover:text-toga-700 dark:hover:text-toga-200 disabled:opacity-50"
                title={t('sync')}
              >
                {syncBusy === a.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
              </button>
              {/* Siempre, no solo en error: cuando el correo de la empresa se
                  movió a Google, la cuenta seguía «activa» contra el buzón
                  antiguo —que ya no recibía nada— y no había forma de
                  reconectarla desde la interfaz. */}
              {canReconnect && (
                <button
                  type="button"
                  onClick={() => setReconnecting(a)}
                  className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-faint hover:text-toga-700 dark:hover:text-toga-200"
                  title={t('reconnect.action')}
                >
                  <Plug size={12} />
                </button>
              )}
            </div>
            {a.status !== 'ACTIVA' && (
              <div className="text-[11px] mt-0.5 text-amber-700 dark:text-amber-300">
                <span className="flex items-center gap-1">
                  <AlertCircle size={10} /> {t('enums:EmailAccountStatus.' + a.status)}
                </span>
                {/* Sin esto, una cuenta con la contraseña caducada no tenía
                    arreglo desde la interfaz: había que borrarla y crearla. */}
                <button
                  type="button"
                  onClick={() => setReconnecting(a)}
                  className="underline hover:no-underline mt-0.5"
                >
                  {t('reconnect.action')}
                </button>
              </div>
            )}
            {a.lastSyncAt && (
              <div className="text-[10px] t-faint mt-0.5">
                {t('lastSync', { date: new Date(a.lastSyncAt).toLocaleString(currentLocaleTag()) })}
              </div>
            )}
          </div>
        ))}

        <div className="mt-4">
          <h3 className="text-xs uppercase tracking-wider t-faint mb-2 px-1">{t('filters')}</h3>
          {(
            [
              { id: 'all' as Filter, label: t('filter.all'), icon: Mail },
              { id: 'unread' as Filter, label: t('filter.unread'), icon: AlertCircle },
              { id: 'archived' as Filter, label: t('filter.archived'), icon: Archive },
              { id: 'unarchived' as Filter, label: t('filter.unarchived'), icon: CheckCircle },
              { id: 'sent' as Filter, label: t('filter.sent'), icon: Send },
            ] satisfies Array<{ id: Filter; label: string; icon: typeof Mail }>
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={[
                'w-full text-left px-2.5 py-1.5 rounded-lg text-sm flex items-center gap-2 transition',
                filter === id ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200' : 't-secondary hover:bg-hover',
              ].join(' ')}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </aside>

      {/* Lista de mensajes */}
      <section className="surface overflow-y-auto">
        <div className="sticky top-0 backdrop-blur bg-topbar px-3 py-2.5 border-b border-base z-10">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full px-3 py-1.5 rounded-full bg-[var(--bg-input)] border border-[var(--border)] text-sm t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40"
          />
        </div>
        {loading && !data ? (
          <div className="p-8 t-muted flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> {t('loading')}
          </div>
        ) : data && data.items.length === 0 ? (
          <p className="p-8 t-muted text-sm text-center">
            {q ? t('noResults') : filter === 'sent' ? t('emptySent') : t('emptyInbox')}
          </p>
        ) : (
          <ul>
            {data?.items.map((m) => (
              <MessageRow
                key={m.id}
                m={m}
                selected={selectedId === m.id}
                onClick={() => setSelectedId(m.id)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Panel de lectura */}
      <section className="surface overflow-hidden">
        <MessageReader
          emailId={selectedId}
          onArchived={() => {
            void loadMessages()
          }}
        />
      </section>

      {showNewAccount && (
        <NewAccountModal
          onClose={() => setShowNewAccount(false)}
          onCreated={() => {
            setShowNewAccount(false)
            void loadAccounts()
            void loadMessages()
          }}
        />
      )}

      <ReconnectAccountModal
        account={reconnecting}
        onClose={() => setReconnecting(null)}
        onReconnected={() => {
          void loadAccounts()
          void loadMessages()
        }}
      />

      <EmailComposer
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        accountId={accountFilter || accounts?.[0]?.id}
      />
    </div>
  )
}

function MessageRow({
  m,
  selected,
  onClick,
}: {
  m: EmailListItem
  selected: boolean
  onClick: () => void
}) {
  const { t } = useTranslation('email')
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={[
          'w-full text-left px-3 py-3 border-b border-base/50 transition',
          selected ? 'bg-toga-500/10' : 'hover:bg-hover/50',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className={[
              'text-sm truncate',
              m.isRead ? 't-secondary' : 't-primary font-semibold',
            ].join(' ')}
          >
            {/* En un enviado el remitente eres tú en TODAS las filas: la lista
                sería tu propio nombre repetido. Se enseña a quién se lo
                mandaste, que es lo que distingue una fila de otra. */}
            {m.direction === 'OUTBOUND'
              ? personas(m.toAddresses) || t('noRecipient')
              : m.fromAddress.replace(/<.+>/, '').trim() || m.fromEmail}
          </span>
          <span className="text-[10px] t-faint shrink-0">
            {new Date(m.receivedAt).toLocaleDateString(currentLocaleTag(), {
              day: '2-digit',
              month: '2-digit',
            })}
          </span>
        </div>
        <div
          className={['text-sm truncate mt-0.5', m.isRead ? 't-secondary' : 't-primary'].join(' ')}
        >
          {m.subject || t('noSubject')}
        </div>
        <div className="text-xs t-faint truncate mt-0.5">{m.snippet}</div>
        <div className="flex items-center gap-2 mt-1">
          {m.hasAttachments && <Paperclip size={11} className="t-faint" />}
          {(m.archivedToOpportunityCode || m.archivedToClientName) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
              <Archive size={9} className="inline mr-0.5" />
              {m.archivedToOpportunityCode ?? m.archivedToClientName}
            </span>
          )}
        </div>
      </button>
    </li>
  )
}
